import {Box} from '@mui/material';
import {selectIsLoggedIn} from '@smartmixin/core';
import {useSelector} from 'react-redux';
import {Outlet, useLocation} from 'react-router';
import AlertSnackbar from '../containers/AlertSnackbar';
import AppNavBar from '../containers/AppNavBar';
import UpdateRequiredBanner from '../containers/UpdateRequiredBanner';
import {HelpUrlLocaleProvider} from '../i18n';
import PageViewTracker from '../lib/PageViewTracker';
import BottomNav from './layout/BottomNav';
import NavRail from './layout/NavRail';
import Sidebar from './layout/Sidebar';
import {FLOATING_ACTION_ATTR} from './layout/floatingAction';
import {MAIN_CONTENT_ID, SkipLink} from './layout/SkipLink';
import {shell} from '../theme/tokens';
import ScrollToTop from './utils/ScrollToTop';
import {useSearchShortcut} from './layout/useSearchShortcut';

// Routes that show the bottom navigation on phones.
const BOTTOM_NAV_ROUTES = ['/app', '/map', '/forecast-maps', '/help'];

const px = (n: number) => `${n}px`;

/** Room a floating action takes: the 44px chip, rounded up. */
const FLOATING_ACTION_HEIGHT = 48;
/** The gap between the snackbar and what it rises above. */
const SNACKBAR_GAP = 8;

/**
 * The app shell (design-system MR-4). The chrome follows sign-in, not the
 * route: signed in = sidebar (≥1200) / icon rail (900–1199) / phone top bar
 * + bottom bar (<900); signed out = the top bar everywhere. Which piece shows
 * is decided by CSS breakpoints, not useMediaQuery, so the prerendered HTML
 * and the first paint are right; display:none pieces leave the
 * accessibility tree.
 *
 * Content offsets are CSS variables set here per breakpoint and read by the
 * one <main> and by pages that size to the viewport (maps, sticky bars):
 * --shell-top, --shell-left, --shell-bottom. Two more place what floats at
 * the bottom right: --fab-bottom (a floating action, 24px above the bottom
 * bar or the window edge) and --snackbar-bottom (the alert snackbar, above
 * the bottom bar, and above a floating action while one is on the page).
 * The latter is a `:has()` rule: a browser without `:has()` (Firefox before
 * 121) still lifts the snackbar above the bottom bar, but not above the
 * floating action, which it may then cover.
 *
 * The alert snackbar sits outside HelpUrlLocaleProvider: on a translated
 * help page (/help/fr/*) its close button stays in the UI language, as the
 * message it closes does. So does the update-required banner (top centre,
 * below --shell-top), which asks a stale tab to reload on every route.
 */
export default function AppContainer() {
  const signedIn = useSelector(selectIsLoggedIn);
  const {pathname} = useLocation();
  useSearchShortcut();
  const showBottomNav = BOTTOM_NAV_ROUTES.some((route) =>
    pathname.startsWith(route),
  );
  const bottom = showBottomNav
    ? `calc(${px(shell.bottomNavHeight)} + env(safe-area-inset-bottom))`
    : '0px';

  return (
    <Box
      sx={{
        '--shell-top': signedIn
          ? {xs: px(shell.topBarHeight), md: '0px'}
          : px(shell.topBarHeight),
        '--shell-left': signedIn
          ? {xs: '0px', md: px(shell.railWidth), lg: px(shell.sidebarWidth)}
          : '0px',
        '--shell-bottom': {xs: bottom, md: '0px'},
        '--fab-bottom': 'calc(var(--shell-bottom) + 24px)',
        '--snackbar-bottom': {
          xs: `calc(var(--shell-bottom) + ${px(SNACKBAR_GAP)})`,
          sm: 'calc(var(--shell-bottom) + 24px)',
        },
        [`&:has([${FLOATING_ACTION_ATTR}])`]: {
          '--snackbar-bottom': `calc(var(--fab-bottom) + ${px(
            FLOATING_ACTION_HEIGHT + SNACKBAR_GAP,
          )})`,
        },
      }}>
      {/* On locale-prefixed help URLs (/help/fr/*, …) the whole page — nav
          bar included — follows the URL's locale instead of the UI
          language. */}
      <HelpUrlLocaleProvider>
        <ScrollToTop />
        <PageViewTracker />
        <SkipLink />
        <AppNavBar signedIn={signedIn} />
        {signedIn && <Sidebar />}
        {signedIn && <NavRail />}
        <Box
          component="main"
          id={MAIN_CONTENT_ID}
          tabIndex={-1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            // dvh (visible viewport) where supported — otherwise map routes,
            // whose content is exactly 100dvh tall, overflow the visible area
            // by the URL-bar delta and leave a dead strip behind the fixed
            // bottom bar on phones.
            '@supports (min-height: 100dvh)': {minHeight: '100dvh'},
            pt: 'var(--shell-top)',
            pl: 'var(--shell-left)',
            pb: 'var(--shell-bottom)',
            outline: 'none',
          }}>
          <Outlet />
        </Box>
        {showBottomNav && <BottomNav />}
      </HelpUrlLocaleProvider>
      <AlertSnackbar />
      <UpdateRequiredBanner />
    </Box>
  );
}
